import { IHttpClient } from '@app/domain/ports';
import pMemoize from 'p-memoize';
import { ICurrencyRateProvider, CurrencyRate } from '@app/domain/ports';

/**
 * NBU API response format (internal to this provider)
 */
interface NBURateResponse {
  exchangedate: string; // "DD.MM.YYYY"
  cc: string; // Currency code (USD, EUR, etc.)
  r030: number; // Currency numeric code
  txt: string; // Currency name in Ukrainian
  rate: number; // Exchange rate
}

/**
 * Currency rate provider that fetches exchange rates from National Bank of Ukraine (NBU).
 * Implements caching with 24-hour TTL to reduce API calls.
 *
 * API: https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange
 */
export class NbuCurrencyRateProvider implements ICurrencyRateProvider {
  private readonly API_BASE = 'https://bank.gov.ua/NBUStatService/v1/statdirectory';

  /**
   * @param httpClient Framework-agnostic HTTP client (injected via DI)
   */
  constructor(private readonly httpClient: IHttpClient) {}

  /**
   * Fetches currency rates for a specific date from NBU API.
   * Results are cached for 24 hours using p-memoize.
   */
  fetchRatesForDate = pMemoize(
    async (date: Date, currencyCodes?: string[]): Promise<CurrencyRate[]> => {
      const dateStr = this.formatDateForAPI(date);
      const url = `${this.API_BASE}/exchange?date=${dateStr}&json`;

      const response = await this.httpClient.get<NBURateResponse[]>(url);

      if (!response || response.length === 0) {
        return [];
      }

      // Parse and filter results
      const results: CurrencyRate[] = response
        .filter((item: NBURateResponse) => {
          // Filter by currency codes if provided
          if (currencyCodes && currencyCodes.length > 0) {
            return currencyCodes.includes(item.cc);
          }
          return true;
        })
        .map((item: NBURateResponse) => ({
          currencyCode: item.cc,
          rate: item.rate,
          date: this.parseDate(item.exchangedate),
        }));

      return results;
    },
    {
      maxAge: 24 * 60 * 60 * 1000, // 24 hours
      cacheKey: ([date, currencyCodes]) => {
        const dateStr = this.formatDateForAPI(date);
        const codes = currencyCodes?.sort().join(',') || 'ALL';
        return `${dateStr}:${codes}`;
      },
    },
  );

  /**
   * Formats date for NBU API URL (YYYYMMDD)
   */
  private formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  /**
   * Parses NBU date format (DD.MM.YYYY) to Date object
   */
  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('.');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
}
